'use client';

import { formatPhoneNumber } from 'src/utils/formatPhoneNumber/formatPhoneNumber';
import { hasContent } from 'src/utils/hasContent/hasContent';
import { createGoogleMapsUrl } from 'src/utils/createGoogleMapsLink';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { Box, Typography } from 'src/components/PulseUI';
import { useMemo } from 'react';
import Link from 'next/link';

const AddressDisplay = ({ isPrimary }: { isPrimary: boolean }) => {
  const { entityDetails, isLoading } = useProfile();

  const primaryAddress = entityDetails?.primary_address;
  const secondaryAddresses = entityDetails?.secondary_addresses;

  const title = isPrimary
    ? 'Primary Address'
    : secondaryAddresses?.length > 1
    ? 'Secondary Addresses'
    : 'Secondary Address';

  // Calculate displayAddresses regardless of loading state to avoid conditional hook calls
  const displayAddresses = useMemo(() => {
    if (!entityDetails) return [];
    return isPrimary
      ? primaryAddress
        ? [primaryAddress]
        : []
      : secondaryAddresses;
  }, [entityDetails, isPrimary, primaryAddress, secondaryAddresses]);

  // Show loading state if data is still loading
  if (isLoading) {
    return (
      <Box className="flex justify-center items-center p-4">
        <div role="progressbar">Loading...</div>
      </Box>
    );
  }

  if (!hasContent(displayAddresses)) {
    return null;
  }

  return (
    <Box key={title} className="flex flex-col">
      <Typography variant="body2" color="tertiary" bold>
        {title}
      </Typography>
      <Box className="flex flex-col gap-2 mt-1">
        {displayAddresses?.map((address, idx) => {
          const formattedPhone =
            address.phone && formatPhoneNumber(address.phone);
          const addressLine1 = address.address_line_1 || '';
          const addressLine2 = address.address_line_2 || '';
          const cityStateZip = `${address.city || ''}, ${address.state || ''} ${
            address.zip_code || ''
          }`;

          const mapsUrl = createGoogleMapsUrl(address);

          return (
            <Box className="mb-2" key={address.address_key || idx}>
              <Link
                href={mapsUrl}
                scroll={false}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline hover:opacity-80 transition-opacity cursor-pointer"
                aria-label={`View ${
                  address.practice_name || 'address'
                } on Google Maps`}
              >
                <Box className="flex flex-col">
                  {address.practice_name && (
                    <Typography variant="body2" bold className="text-link">
                      {address.practice_name}
                    </Typography>
                  )}
                  <Typography variant="body2" bold className="text-link">
                    {addressLine1}
                  </Typography>
                  {addressLine2 && (
                    <Typography variant="body2" bold className="text-link">
                      {addressLine2}
                    </Typography>
                  )}
                  <Typography variant="body2" bold className="text-link">
                    {cityStateZip}
                  </Typography>
                </Box>
              </Link>
              {formattedPhone && (
                <Typography variant="body2" color="primary" bold className="mt-1">
                  {formattedPhone}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AddressDisplay;
